import { escapeHTML } from '../../utils/escape.js';
import { formatPrice } from '../../utils/formatters.js';

/**
 * Vista de Empleados del panel de staff (Control de Horarios y Liquidación de
 * Sueldos, slice 1). Función de render pura: `js/admin.js` (wiring) le pasa
 * `{ empleados }` (de `admin-empleados.service.js#loadEmpleados`) + un
 * ui-state (loading/error/ready, empleado en edición, diálogo de PIN).
 *
 * `valor` (Req: Sensitive Field Isolation) SÍ se renderiza acá a propósito:
 * esta es la única vista admin-only del recurso, gateada por RLS antes de
 * llegar al navegador — no un ocultamiento visual. `pin`/`pin_hash` nunca
 * llegan en la práctica (el servicio los excluye) y este render tampoco los
 * interpola aunque vinieran presentes por error. El `<input name="pin">`
 * (`renderPinDialog`) nunca lleva `value`: es de solo-escritura, jamás
 * round-tripea un PIN en texto plano (Req: PIN Uniqueness and Hashing).
 */

const FIELD =
    'w-full bg-base border border-border-subtle rounded-2xl px-4 py-3 text-sm text-text-primary focus:outline-none focus:border-brand';
const LABEL = 'block text-[10px] font-black uppercase tracking-[0.2em] text-text-muted mb-2';

/**
 * ¿`customer` tiene la "marca de empleado"? (Req: "Fudo Customer Missing the
 * Employee Marker Warns, Never Blocks", Q5). Exactamente `houseAccountEnabled
 * === true && discountPercentage > 0` — sin `active`, que es un filtro de
 * LISTADO por defecto, no parte de la marca en sí.
 * @param {{ houseAccountEnabled?: boolean, discountPercentage?: number }} customer
 * @returns {boolean}
 */
function hasEmployeeMarker(customer) {
    return customer?.houseAccountEnabled === true && Number(customer?.discountPercentage) > 0;
}

/**
 * CERO SILENCIOSO (coordinator, 2026-08-08): si `selectedId` no aparece en
 * NINGUNA respuesta de Fudo (`allItems`, sin filtrar) — no solo filtrada
 * afuera — el picker NUNCA puede caer a "Sin asignar": en `api/liquidacion.js`
 * un id ausente es una referencia legítimamente vacía (`consumos: 0`), así
 * que perder el id acá es sobrepago silencioso en la próxima liquidación.
 * Devuelve una entrada SINTÉTICA con el snapshot `*_nombre` (migración 21,
 * guardado justamente para este caso) en vez de perder el vínculo.
 * @param {Array<object>} allItems TODO lo que devolvió Fudo, sin filtrar
 * @param {string} selectedId
 * @param {string} selectedNombreSnapshot
 * @returns {{id: string, name: string, missingFromFudo: true}|null}
 */
function missingSelectionEntry(allItems, selectedId, selectedNombreSnapshot) {
    if (!selectedId) return null;
    if (allItems.some((i) => String(i.id) === String(selectedId))) return null;
    return { id: selectedId, name: selectedNombreSnapshot || selectedId, missingFromFudo: true };
}

/**
 * Opciones del picker de cliente Fudo: por defecto solo `active &&
 * houseAccountEnabled && discountPercentage > 0` (design.md "Pickers");
 * `mostrarTodos` agrega el resto. El cliente YA seleccionado se incluye
 * SIEMPRE, aunque no matchee el filtro vigente o ya no esté en NINGUNA
 * respuesta de Fudo (ver `missingSelectionEntry`) — si no, un repintado con
 * "mostrar todos" desmarcado, o un directorio Fudo que dejó de traerlo,
 * haría desaparecer en silencio lo ya elegido.
 * @param {Array<object>} customers
 * @param {string} selectedId
 * @param {boolean} mostrarTodos
 * @param {string} selectedNombreSnapshot
 * @returns {Array<object>}
 */
function customerPickerOptions(customers, selectedId, mostrarTodos, selectedNombreSnapshot) {
    const filtered = customers.filter((c) => c.active !== false && hasEmployeeMarker(c));
    const base = mostrarTodos ? customers : filtered;
    const yaIncluido = base.some((c) => String(c.id) === String(selectedId));
    if (selectedId && !yaIncluido) {
        const selected = customers.find((c) => String(c.id) === String(selectedId));
        const entry = selected ?? missingSelectionEntry(customers, selectedId, selectedNombreSnapshot);
        if (entry) return [...base, entry];
    }
    return base;
}

function renderPickerOptions(items, selectedId) {
    return items
        .map((item) => {
            const label = item.missingFromFudo ? `${item.name} (ya no aparece en Fudo)` : item.name;
            return `<option value="${escapeHTML(item.id)}" data-nombre="${escapeHTML(item.name)}" ${String(item.id) === String(selectedId) ? 'selected' : ''}>${escapeHTML(label)}</option>`;
        })
        .join('');
}

/**
 * Aviso prominente (CERO SILENCIOSO, coordinator 2026-08-08) cuando el id
 * asignado ya no aparece en NINGUNA respuesta de Fudo — distinto del aviso
 * de "sin marca de empleado": acá puede ser un cliente/proveedor borrado, o
 * una lectura incompleta, y las dos ameritan que el admin mire. El vínculo
 * se mantiene (nunca se convierte en "Sin asignar" solo por esto).
 *
 * `directoryUnavailable` (review follow-up P2, 2026-08-08) distingue "no
 * pudimos consultar Fudo" (falla de red/401/rate-limit — el directorio
 * completo quedó sin cargar) de "genuinely absent" (el directorio SÍ cargó y
 * este id no está). Sin esta distinción, una caída de Fudo hacía que ESTE
 * aviso disparara "ya no aparece" para TODOS los empleados con id asignado —
 * una alarma falsa masiva, no un hallazgo real.
 * @param {string} testId
 * @param {Array<object>} allItems
 * @param {string} selectedId
 * @param {string} selectedNombreSnapshot
 * @param {boolean} [directoryUnavailable]
 * @returns {string}
 */
function renderMissingFromFudoWarning(testId, allItems, selectedId, selectedNombreSnapshot, directoryUnavailable = false) {
    const missing = missingSelectionEntry(allItems, selectedId, selectedNombreSnapshot);
    if (!missing) return '';
    const message = directoryUnavailable
        ? `No pudimos consultar Fudo ahora mismo. Se mantiene el vínculo guardado con "${escapeHTML(missing.name)}" — no significa que se haya borrado.`
        : `Atención: "${escapeHTML(missing.name)}" ya no aparece en el listado de Fudo (¿se borró? ¿la lectura vino incompleta?). El vínculo se mantiene igual — solo se borra si lo cambiás a mano.`;
    return `
        <p data-testid="${testId}" role="alert" class="mt-2 px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/40 text-xs font-bold text-red-600 dark:text-red-400">
            ${message}
        </p>
    `;
}

/**
 * Aviso prominente (Req: "warns, never blocks", Q5) cuando el cliente Fudo
 * elegido no tiene la marca de empleado. `role="alert"` + estilo destacado:
 * nunca un tooltip silencioso. NO se muestra si no hay cliente elegido, o si
 * el elegido todavía no está en el listado cargado (no hay forma de saberlo).
 * @param {Array<object>} fudoCustomers
 * @param {string} selectedId
 * @returns {string}
 */
function renderMarkerWarning(fudoCustomers, selectedId) {
    if (!selectedId) return '';
    const selected = fudoCustomers.find((c) => String(c.id) === String(selectedId));
    if (!selected || hasEmployeeMarker(selected)) return '';
    return `
        <p data-testid="fudo-cliente-marker-warning" role="alert" class="mt-2 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/40 text-xs font-bold text-amber-600 dark:text-amber-400">
            Atención: "${escapeHTML(selected.name)}" no tiene la marca de empleado en Fudo (cuenta corriente + descuento). Se puede guardar igual, pero confirmá que sea la persona correcta.
        </p>
    `;
}

function renderForm(editing, uiState, fudoCustomers) {
    // El modo lo define tener un `id`, no que `editing` sea un objeto: durante
    // un alta, `js/admin.js` pasa acá el borrador de lo tipeado (para que un
    // repintado no lo borre) y ese borrador es un objeto sin `id`. Chequear
    // solo truthiness haría que el form de alta se rotule como edición.
    const isEdit = !!editing?.id;
    const v = (key, fallback = '') => editing?.[key] ?? fallback;
    // AD3: un sueldo fijo nunca se duplica por feriado — el checkbox se
    // muestra deshabilitado (con nota) en vez de ocultarse, porque el campo
    // es IGNORADO server-side, no RECHAZADO: sigue siendo un dato valido que
    // simplemente no tiene efecto en el bruto de un empleado fijo.
    const esFijo = v('tipo_pago') === 'fijo';

    return `
        <form class="bg-surface border border-border-subtle rounded-2xl p-6 mb-6" data-mode="${isEdit ? 'editar' : 'crear'}">
            <input type="hidden" name="id" value="${escapeHTML(v('id'))}" />
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
                <div>
                    <label class="${LABEL}">Nombre</label>
                    <input type="text" name="nombre" required value="${escapeHTML(v('nombre'))}" class="${FIELD}" />
                </div>
                <div>
                    <label class="${LABEL}">Tipo de pago</label>
                    <select name="tipo_pago" required class="${FIELD}">
                        <option value="">Elegir...</option>
                        <option value="hora" ${v('tipo_pago') === 'hora' ? 'selected' : ''}>Por hora</option>
                        <option value="dia" ${v('tipo_pago') === 'dia' ? 'selected' : ''}>Por día</option>
                        <option value="fijo" ${esFijo ? 'selected' : ''}>Fijo (sueldo mensual)</option>
                    </select>
                </div>
                <div>
                    <label class="${LABEL}">${esFijo ? 'Valor (monto del período liquidado, sin prorrateo)' : 'Valor'}</label>
                    <input type="number" name="valor" required min="0" step="0.01" value="${escapeHTML(String(v('valor')))}" class="${FIELD}" />
                </div>
                <div>
                    <label class="${LABEL}">Foto (URL)</label>
                    <input type="url" name="foto_url" value="${escapeHTML(v('foto_url'))}" class="${FIELD}" />
                </div>
                <div>
                    <label class="${LABEL}">Cliente Fudo</label>
                    <select name="fudo_cliente_id" class="${FIELD}">
                        <option value="">Sin asignar</option>
                        ${renderPickerOptions(customerPickerOptions(fudoCustomers, v('fudo_cliente_id'), !!uiState.mostrarTodosClientes, v('fudo_cliente_nombre')), v('fudo_cliente_id'))}
                    </select>
                    <label class="flex items-center gap-2 mt-2 text-[10px] text-text-muted">
                        <input type="checkbox" data-action="empleado-mostrar-todos-clientes" ${uiState.mostrarTodosClientes ? 'checked' : ''} class="w-3.5 h-3.5 rounded border-border-subtle" />
                        Mostrar todos los clientes de Fudo
                    </label>
                    ${renderMarkerWarning(fudoCustomers, v('fudo_cliente_id'))}
                    ${renderMissingFromFudoWarning('fudo-cliente-missing-warning', fudoCustomers, v('fudo_cliente_id'), v('fudo_cliente_nombre'), uiState.fudoDirectoryUnavailable)}
                </div>
                <div class="flex items-center gap-2 sm:col-span-2 lg:col-span-3">
                    <input type="checkbox" name="paga_feriado_doble" id="empleado-feriado-doble" ${v('paga_feriado_doble') ? 'checked' : ''} ${esFijo ? 'disabled' : ''} class="w-4 h-4 rounded border-border-subtle text-brand focus:ring-brand ${esFijo ? 'opacity-40 cursor-not-allowed' : ''}" />
                    <label for="empleado-feriado-doble" class="text-sm ${esFijo ? 'text-text-muted' : 'text-text-primary'}">Paga doble en feriado</label>
                </div>
                ${esFijo ? '<p class="text-[10px] text-text-muted sm:col-span-2 lg:col-span-3 -mt-3">Ignorado en sueldo fijo: nunca se duplica por feriado (AD3).</p>' : ''}
            </div>
            <div class="flex gap-3">
                <button type="submit" data-action="empleado-guardar" class="px-8 py-3 bg-brand text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg active:scale-95 transition-transform disabled:opacity-60 disabled:pointer-events-none">
                    ${isEdit ? 'Guardar cambios' : 'Crear empleado'}
                </button>
                ${isEdit ? '<button type="button" data-action="empleado-cancelar-edicion" class="px-8 py-3 rounded-2xl border border-border-subtle text-xs font-black uppercase tracking-widest text-text-muted">Cancelar</button>' : ''}
            </div>
        </form>
    `;
}

function renderRow({ id, nombre, foto_url, tipo_pago, valor, activo }) {
    const safeId = escapeHTML(id);
    const tipoLabel = tipo_pago === 'fijo' ? 'Fijo' : tipo_pago === 'dia' ? 'Por día' : 'Por hora';
    const toggleLabel = activo ? 'Desactivar' : 'Activar';
    const avatar = foto_url
        ? `<img src="${escapeHTML(foto_url)}" alt="" class="w-10 h-10 rounded-xl object-cover shrink-0" />`
        : `<span class="w-10 h-10 rounded-xl bg-black/5 dark:bg-white/10 flex items-center justify-center shrink-0"><i class="fa-solid fa-user text-text-muted" aria-hidden="true"></i></span>`;

    return `
        <div class="flex items-center justify-between gap-4 py-4 border-b border-border-subtle last:border-b-0">
            <div class="flex items-center gap-4 flex-1 min-w-0">
                ${avatar}
                <div class="min-w-0">
                    <p class="font-bold text-text-primary truncate">${escapeHTML(nombre)}</p>
                    <p class="text-xs text-text-muted mt-1 truncate">
                        ${tipoLabel} · ${formatPrice(valor)} · <span class="${activo ? 'text-brand' : 'text-text-muted'}">${activo ? 'Activo' : 'Inactivo'}</span>
                    </p>
                </div>
            </div>
            <div class="flex shrink-0 gap-2">
                <button data-action="empleado-editar" data-id="${safeId}" class="px-4 py-2 rounded-xl border border-border-subtle text-xs font-black uppercase tracking-widest text-text-muted hover:text-brand transition-colors">Editar</button>
                <button data-action="empleado-pin-abrir" data-id="${safeId}" class="px-4 py-2 rounded-xl border border-border-subtle text-xs font-black uppercase tracking-widest text-text-muted hover:text-brand transition-colors">PIN</button>
                <button data-action="empleado-toggle-activo" data-id="${safeId}" class="px-4 py-2 rounded-xl border border-border-subtle text-xs font-black uppercase tracking-widest text-text-muted hover:text-brand transition-colors">${toggleLabel}</button>
            </div>
        </div>
    `;
}

/** El `<input name="pin">` nunca lleva `value`: ver nota de cabecera del archivo. */
function renderPinDialog(pinDialog, empleados) {
    if (!pinDialog?.empleadoId) return '';
    const nombre = escapeHTML(empleados.find((e) => e.id === pinDialog.empleadoId)?.nombre ?? '');
    const errorHtml = pinDialog.error
        ? `<p class="text-xs text-red-500 font-bold mt-3" role="alert">${escapeHTML(pinDialog.error)}</p>`
        : '';

    return `
        <div role="dialog" aria-modal="true" aria-labelledby="empleado-pin-title" class="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 px-6">
            <div class="bg-base rounded-[2rem] p-8 max-w-sm w-full shadow-2xl">
                <p id="empleado-pin-title" class="text-sm font-bold text-text-primary text-center mb-6">Nuevo PIN para ${nombre}</p>
                <form data-id="${escapeHTML(pinDialog.empleadoId)}">
                    <label class="${LABEL}">PIN (4 dígitos)</label>
                    <input type="password" name="pin" inputmode="numeric" autocomplete="off" class="${FIELD}" />
                    ${errorHtml}
                    <div class="flex gap-3 mt-6">
                        <button type="button" data-action="empleado-pin-cancelar" class="flex-1 py-4 rounded-2xl border border-border-subtle text-xs font-black uppercase tracking-widest text-text-muted">Cancelar</button>
                        <button type="submit" data-action="empleado-pin-guardar" class="flex-1 py-4 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg">Guardar</button>
                    </div>
                </form>
            </div>
        </div>
    `;
}

/**
 * S8 (design.md/tasks.md, spec `payroll-advances` "fudo_proveedor_id
 * Ignored, Not Dropped"): el form YA NO ofrece `fudo_proveedor_id` — la
 * columna (y sus valores ya guardados) sigue existiendo en `empleados`, sin
 * usarse en ningún camino de lectura, para que la reconciliación histórica
 * contra el ledger de gastos de Fudo pre-cutover (S4, `api/_lib/fudo/
 * expenses-ledger.js`, dejado en disco sin importar) siga siendo posible.
 * `js/admin.js#readEmpleadoForm` tampoco lee ni manda esa clave — omitirla
 * del payload de `updateEmpleado` deja la columna INTACTA en la fila (un
 * `.update()` de Supabase sólo toca las claves presentes), en vez de
 * pisarla con `null` en el próximo guardado de cualquier otro campo. `viewModel
 * .fudoProviders` sigue llegando desde `js/admin.js` (el fetch a Fudo
 * `/providers` no se tocó, fuera de este alcance) pero esta vista ya no lo
 * consume — sin field, sin picker.
 * @param {{ empleados: Array<object>, fudoCustomers?: Array<object>, fudoProviders?: Array<object> }|null} viewModel usado solo en `status: 'ready'`.
 * @param {{ status: 'loading'|'error'|'ready', editing?: object|null, pinDialog?: { empleadoId: string, error?: string }|null, mostrarTodosClientes?: boolean, fudoDirectoryUnavailable?: boolean }} [uiState]
 * @returns {string} HTML de la vista completa (form + lista + diálogo de PIN).
 */
export function EmpleadosView(viewModel, uiState = {}) {
    if (uiState.status === 'loading') {
        return `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="status" aria-live="polite">
                <i class="fa-solid fa-circle-notch fa-spin text-2xl text-brand mb-4" aria-hidden="true"></i>
                <p class="text-xs font-black text-text-muted uppercase tracking-widest">Cargando empleados...</p>
            </div>
        `;
    }

    if (uiState.status === 'error') {
        return `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="alert">
                <p class="text-sm font-bold text-text-primary mb-6">No pudimos cargar los empleados. Probá de nuevo.</p>
                <button data-action="empleado-retry" class="px-6 py-3 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg">Reintentar</button>
            </div>
        `;
    }

    const empleados = viewModel?.empleados ?? [];
    const fudoCustomers = viewModel?.fudoCustomers ?? [];
    const listHtml =
        empleados.length > 0
            ? empleados.map(renderRow).join('')
            : '<p class="text-xs text-text-muted py-4">No hay empleados cargados.</p>';

    return `
        <section aria-label="Empleados">
            <h2 class="text-lg font-black uppercase tracking-tight text-text-primary mb-5">Empleados</h2>
            ${renderForm(uiState.editing ?? null, uiState, fudoCustomers)}
            <div class="bg-surface border border-border-subtle rounded-2xl px-5">${listHtml}</div>
            ${renderPinDialog(uiState.pinDialog, empleados)}
        </section>
    `;
}
